import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, CreditCard } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface CreditLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  userCredits: number;
  minRequiredCredits: number;
  onGetMoreCredits: () => void;
}

const CreditLimitModal: React.FC<CreditLimitModalProps> = ({
  isOpen,
  onClose,
  userCredits,
  minRequiredCredits,
  onGetMoreCredits
}) => {
  const { t } = useTranslation();
  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose]);

  // Prevent clicks on the dialog from closing the modal
  const handleDialogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 overflow-y-auto"
        >
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm" 
            onClick={onClose}
          />

          {/* Dialog ? bottom-sheet on mobile, centered on desktop */}
          <div className="min-h-screen px-4 py-4 pb-[env(safe-area-inset-bottom)] flex items-end sm:items-center justify-center">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="dark-container w-full max-w-lg relative rounded-t-2xl sm:rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto"
              onClick={handleDialogClick}
            >
              {/* Icon */}
              <div className="flex justify-center -mt-10">
                <div className="vscode-bg-secondary rounded-full p-2">
                  <div className="bg-red-900/30 rounded-full p-3">
                    <AlertTriangle className="w-10 h-10 text-red-500" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 text-center">
                <h3 className="text-2xl font-bold vscode-text-primary mb-4">
                  {t('creditsLimitModal.title')}
                </h3>
                
                <p className="vscode-text-secondary mb-4">
                  {t('creditsLimitModal.description', { count: userCredits })}
                </p>

                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 mb-4 text-left">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                    <p className="text-yellow-300 text-xs">
                      {t('creditsLimitModal.requiredCredits', { count: minRequiredCredits })}
                    </p>
                  </div>
                </div>

                <p className="vscode-text-secondary mb-4">
                  {t('creditsLimitModal.toContinue')}
                </p>

                <ul className="space-y-2 vscode-text-secondary mb-6 text-left mx-auto max-w-md">
                  <li className="flex items-start space-x-2">
                    <span className="text-cyan-400 mt-0.5">•</span>
                    <span>{t('creditsLimitModal.optionSingle')}</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-green-400 mt-0.5">•</span>
                    <span>{t('creditsLimitModal.optionGetMore')}</span>
                  </li>
                </ul>

                {/* Buttons — 44px touch targets */}
                <div className="flex flex-col sm:flex-row justify-center gap-3 mt-6">
                  <button
                    onClick={onClose}
                    className="dark-button-secondary min-h-[44px] px-6 py-2.5 flex-1 sm:flex-none touch-manipulation"
                  >
                    {t('creditsLimitModal.cancel')}
                  </button>
                  
                  <button
                    onClick={onGetMoreCredits}
                    className="dark-button-primary min-h-[44px] px-6 py-2.5 flex items-center justify-center space-x-2 flex-1 sm:flex-none touch-manipulation"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>{t('creditsLimitModal.getMoreCredits')}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default React.memo(CreditLimitModal);
